import type { Account, AccountBranding, UpdateAccountBrandingRequest, User } from '@deskworks/shared-types';
import { useQueryClient } from '@tanstack/react-query';
import { ImageUp, Inbox, Layers, RotateCcw, Sparkles, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Logo } from '@/components/logo';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { logoColors, setBrandPreview, suggestedPrimary } from '@/lib/brand';
import { useApiMutation } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { useToast } from '@/lib/toast';
import { cn } from '@/lib/utils';

/** Sides of the logo as stored: an icon in the sidebar and the tab, sharp on high-density screens. */
const LOGO_SIDE = 256;
/** What the server takes (docs/features/branding.md). */
const LOGO_MAX_BYTES = 512 * 1024;
const HEX = /^#?([0-9a-f]{6})$/i;

type NewLogo = { dataBase64: string; mimeType: 'image/png' | 'image/webp'; url: string };

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('unreadable'));
    img.src = src;
  });
}

/** A picture drawn at most LOGO_SIDE on its longer side (an SVG at exactly that), with the colors it is made of. */
function drawLogo(img: HTMLImageElement, vector: boolean) {
  const w0 = img.naturalWidth || LOGO_SIDE;
  const h0 = img.naturalHeight || LOGO_SIDE;
  const scale = vector ? LOGO_SIDE / Math.max(w0, h0) : Math.min(1, LOGO_SIDE / Math.max(w0, h0));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(w0 * scale));
  canvas.height = Math.max(1, Math.round(h0 * scale));
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return { canvas, colors: logoColors(ctx.getImageData(0, 0, canvas.width, canvas.height)) };
}

/** An uploaded picture as the logo is stored: scaled down, as PNG (WebP if a photo is too large as PNG). */
async function readLogo(file: File): Promise<{ logo: NewLogo; colors: string[] }> {
  const objectUrl = URL.createObjectURL(file);
  try {
    const { canvas, colors } = drawLogo(await loadImage(objectUrl), file.type === 'image/svg+xml');
    let url = canvas.toDataURL('image/png');
    let mimeType: NewLogo['mimeType'] = 'image/png';
    if (url.length * 0.75 > LOGO_MAX_BYTES) {
      url = canvas.toDataURL('image/webp', 0.9);
      mimeType = 'image/webp';
    }
    return { logo: { dataBase64: url.slice(url.indexOf(',') + 1), mimeType, url }, colors };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * The account's look (docs/features/branding.md): its logo, its main color and its sidebar's color. Account admins
 * change it; the whole app shows the draft while the dialog is open, and a new logo offers its colors.
 */
export function AccountBrandingDialog({ account, open, onOpenChange }: { account: Account; open: boolean; onOpenChange: (open: boolean) => void }) {
  const saved = account.branding;
  const queryClient = useQueryClient();
  const toast = useToast();
  const fileInput = useRef<HTMLInputElement>(null);
  const [primary, setPrimary] = useState<string | null>(saved?.primaryColor ?? null);
  const [sidebar, setSidebar] = useState<string | null>(saved?.sidebarColor ?? null);
  /** undefined: the saved logo stays. */
  const [logo, setLogo] = useState<NewLogo | null | undefined>(undefined);
  const [colors, setColors] = useState<string[]>([]);
  /** Colors of a logo just uploaded, offered until taken or dismissed. */
  const [offer, setOffer] = useState<string[] | null>(null);
  const logoUrl = logo === undefined ? (saved?.logoUrl ?? null) : (logo?.url ?? null);

  // Each opening starts from what is saved, with the saved logo's colors at hand.
  useEffect(() => {
    if (!open) return;
    setPrimary(saved?.primaryColor ?? null);
    setSidebar(saved?.sidebarColor ?? null);
    setLogo(undefined);
    setOffer(null);
    setColors([]);
    let live = true;
    if (saved?.logoUrl)
      loadImage(saved.logoUrl)
        .then((img) => live && setColors(drawLogo(img, false).colors))
        .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [open, saved?.primaryColor, saved?.sidebarColor, saved?.logoUrl]);

  useEffect(() => {
    setBrandPreview(open ? { primaryColor: primary, sidebarColor: sidebar, logoUrl } : null);
  }, [open, primary, sidebar, logoUrl]);
  useEffect(() => () => setBrandPreview(null), []);

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast.error(t('That is not a picture'), t('Use a PNG, JPEG, WebP or SVG.'));
    if (file.size > 10 * 1024 * 1024) return toast.error(t('The picture is too large'), t('10 MB at most.'));
    try {
      const read = await readLogo(file);
      setLogo(read.logo);
      setColors(read.colors);
      setOffer(read.colors.length ? read.colors : null);
    } catch {
      toast.error(t('That picture can’t be read'), t('Use a PNG, JPEG, WebP or SVG.'));
    }
  };

  const removeLogo = () => {
    setLogo(saved?.logoUrl ? null : undefined);
    setColors([]);
    setOffer(null);
  };

  /** Back to the Deskworks look: no colors, no logo. */
  const plain = () => {
    setPrimary(null);
    setSidebar(null);
    removeLogo();
  };

  const takeOffer = () => {
    const main = offer && suggestedPrimary(offer);
    if (main) setPrimary(main);
    setOffer(null);
  };

  const changes: UpdateAccountBrandingRequest = {
    ...(primary !== (saved?.primaryColor ?? null) && { primaryColor: primary }),
    ...(sidebar !== (saved?.sidebarColor ?? null) && { sidebarColor: sidebar }),
    ...(logo !== undefined && { logo: logo && { dataBase64: logo.dataBase64, mimeType: logo.mimeType } }),
  };
  const changed = Object.keys(changes).length > 0;

  const save = useApiMutation(() => api.accounts.updateBranding(account.id, changes), {
    success: t('Look saved'),
    onSuccess: (branding: AccountBranding | null) => {
      // The new look before the dialog closes, so the app doesn't flash the old one while it reloads.
      queryClient.setQueryData<{ user: User; accounts: Account[] }>(qk.me, (me) => me && { ...me, accounts: me.accounts.map((a) => (a.id === account.id ? { ...a, branding } : a)) });
      void queryClient.invalidateQueries({ queryKey: qk.me });
      onOpenChange(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('Colors & logo')}</DialogTitle>
          <DialogDescription>{t('How Deskworks looks for everyone in {account}. You see the change right away; it holds once saved.', { account: account.name })}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-2">
          <Label>{t('Logo')}</Label>
          <div className="flex items-center gap-3">
            <div className="grid size-14 shrink-0 place-items-center rounded-xl border bg-muted/40 p-1.5">
              <Logo src={logoUrl} className="size-full" />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => fileInput.current?.click()}>
                <ImageUp /> {logoUrl ? t('Replace logo') : t('Upload logo')}
              </Button>
              {logoUrl && (
                <Button type="button" variant="ghost" size="sm" onClick={removeLogo}>
                  <Trash2 /> {t('Remove')}
                </Button>
              )}
            </div>
            <input
              ref={fileInput}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
              className="hidden"
              onChange={(e) => {
                void onFile(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
          </div>
          <p className="text-xs text-muted-foreground">{t('Shown at the top of the sidebar and as the browser tab’s icon. A square icon works best.')}</p>
        </div>

        {offer && (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-primary/30 bg-accent/60 px-3 py-2.5 text-sm">
            <Sparkles className="size-4 shrink-0 text-primary" />
            <div className="min-w-0 flex-1">
              <div className="font-medium">{t('Colors from your logo')}</div>
              <div className="mt-1.5 flex gap-1.5">
                {offer.map((c) => (
                  <span key={c} className="size-5 rounded-full border border-black/10 dark:border-white/15" style={{ background: c }} title={c} />
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => setOffer(null)}>
                {t('Keep mine')}
              </Button>
              <Button type="button" size="sm" onClick={takeOffer}>
                {t('Use these colors')}
              </Button>
            </div>
          </div>
        )}

        <ColorField
          label={t('Main color')}
          hint={t('Buttons, links and highlights. It is made lighter or darker where it wouldn’t read, e.g. in the dark theme.')}
          value={primary}
          onChange={setPrimary}
          colors={colors}
          empty={t('Deskworks indigo')}
        />
        <ColorField
          label={t('Sidebar')}
          hint={t('The sidebar’s background; its text turns light or dark to match. In the dark theme it stays dark, in your color’s hue.')}
          value={sidebar}
          onChange={setSidebar}
          colors={colors}
          empty={t('Neutral')}
        />

        <div className="grid gap-2">
          <Label>{t('Preview')}</Label>
          <Preview name={account.name} logoUrl={logoUrl} />
        </div>

        <DialogFooter className="sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            onClick={plain}
            disabled={!primary && !sidebar && !logoUrl}
          >
            <RotateCcw /> {t('Deskworks look')}
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('Cancel')}
            </Button>
            <Button type="button" loading={save.isPending} disabled={!changed} onClick={() => save.mutate()}>
              {t('Save')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** A color: picker, #rrggbb, the logo's colors to pick from, and back to the default. */
function ColorField({
  label,
  hint,
  value,
  onChange,
  colors,
  empty,
}: {
  label: string;
  hint: string;
  value: string | null;
  onChange: (value: string | null) => void;
  colors: string[];
  empty: string;
}) {
  const [text, setText] = useState(value ?? '');
  useEffect(() => setText(value ?? ''), [value]);
  return (
    <div className="grid gap-2">
      <Label>{label}</Label>
      <div className="flex flex-wrap items-center gap-2">
        <label
          className={cn('relative size-9 shrink-0 cursor-pointer overflow-hidden rounded-md border shadow-xs', !value && 'bg-[repeating-linear-gradient(45deg,var(--muted),var(--muted)_4px,var(--background)_4px,var(--background)_8px)]')}
          style={value ? { background: value } : undefined}
          title={t('Pick a color')}
        >
          <input type="color" value={value ?? '#4f46e5'} onChange={(e) => onChange(e.target.value)} className="absolute inset-0 size-full cursor-pointer opacity-0" aria-label={label} />
        </label>
        <Input
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            const m = HEX.exec(e.target.value.trim());
            if (m) onChange(`#${m[1].toLowerCase()}`);
          }}
          onBlur={() => setText(value ?? '')}
          placeholder={empty}
          className="w-40 font-mono text-sm"
          spellCheck={false}
        />
        {colors.length > 0 && (
          <div className="flex items-center gap-1.5" role="group" aria-label={t('Colors from your logo')}>
            {colors.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => onChange(c)}
                className={cn(
                  'size-6 cursor-pointer rounded-full border border-black/10 transition-transform hover:scale-110 dark:border-white/15',
                  value === c && 'ring-2 ring-ring ring-offset-2 ring-offset-background',
                )}
                style={{ background: c }}
                title={c}
                aria-label={c}
                aria-pressed={value === c}
              />
            ))}
          </div>
        )}
        {value && (
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)}>
            {t('Default')}
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

/** A small app with the draft's tokens: the sidebar with logo and a selected entry, a page with a link and a button. */
function Preview({ name, logoUrl }: { name: string; logoUrl: string | null }) {
  return (
    <div className="flex min-h-36 overflow-hidden rounded-lg border text-xs" aria-hidden>
      <div className="brand-sidebar flex w-36 shrink-0 flex-col gap-1 border-r border-sidebar-border bg-sidebar p-2 text-sidebar-foreground">
        <div className="mb-1 flex min-w-0 items-center gap-1.5">
          <Logo src={logoUrl} className="size-6" />
          <span className="truncate font-semibold text-foreground">{name}</span>
        </div>
        <div className="flex items-center gap-1.5 rounded-md bg-sidebar-accent px-1.5 py-1 font-medium text-foreground">
          <Layers className="size-3.5 text-primary" /> {t('Desks')}
        </div>
        <div className="flex items-center gap-1.5 px-1.5 py-1 text-sidebar-foreground/80">
          <Inbox className="size-3.5 text-muted-foreground" /> {t('Inbox')}
        </div>
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-2 bg-background p-3">
        <div className="text-sm font-semibold text-foreground">{t('Desks')}</div>
        <div className="text-muted-foreground">
          {t('Two Work Items wait for you.')} <span className="font-medium text-primary underline-offset-2">{t('Open')}</span>
        </div>
        <div className="mt-auto flex flex-wrap gap-2">
          <span className="rounded-md bg-primary px-2.5 py-1.5 font-medium text-primary-foreground">{t('Create Work Item')}</span>
          <span className="rounded-md bg-accent px-2.5 py-1.5 text-accent-foreground">{t('Selected')}</span>
        </div>
      </div>
    </div>
  );
}
